import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useInstatDesign } from './theme/instatDesign';
import { Card, Pill, SegmentedControl, KpiTile } from './ui';
import {
  ZONES,
  SVG_W,
  SVG_H,
  shotToSvg,
  classifyShot,
} from './instatShotZones';

const COURT_BG = '/instat/basketball-field__FIBA.png';

// Hudl renders zone polygons as semi-transparent white overlays on the court PNG.
const ZONE_FILL = '#ffffff';
const ZONE_OPACITY = 0.45;
const ZONE_STROKE = '#000000';
const ZONE_STROKE_W = 0.5;

// Shot dot styling (matches Hudl's "Shots on court" view).
const DOT_MADE_FILL = '#1a1a1a';
const DOT_MISS_FILL = 'transparent';
const DOT_STROKE = '#1a1a1a';
const DOT_R = 5;

// =============================================================================

function ZonePoly({ zone }) {
  if (zone.kind === 'rect') {
    return (
      <rect
        id={zone.id}
        x={zone.rect.x}
        y={zone.rect.y}
        width={zone.rect.width}
        height={zone.rect.height}
        fill={ZONE_FILL}
        opacity={ZONE_OPACITY}
        stroke={ZONE_STROKE}
        strokeWidth={ZONE_STROKE_W}
      />
    );
  }
  return (
    <path
      id={zone.id}
      d={zone.d}
      fill={ZONE_FILL}
      opacity={ZONE_OPACITY}
      stroke={ZONE_STROKE}
      strokeWidth={ZONE_STROKE_W}
    />
  );
}

function ZoneLabel({ zone, stats }) {
  if (!stats || stats.att === 0) return null;
  // Hudl uses (label.left, label.top) AS the center of the text — width/height
  // are only the click/hover hit-area, not the anchor.
  const cx = zone.label.left;
  const cy = zone.label.top;
  const pct = Math.round((stats.made / stats.att) * 100);
  return (
    <g style={{ pointerEvents: 'none' }}>
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="600"
        fill="#1f1208"
        fontFamily="Helvetica, Arial, sans-serif"
      >
        {pct}%
      </text>
      <text
        x={cx}
        y={cy + 13}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="9"
        fill="#5a4528"
        fontFamily="Helvetica, Arial, sans-serif"
      >
        {stats.made} — {stats.att}
      </text>
    </g>
  );
}

function ShotDot({ shot }) {
  const { sx, sy } = shotToSvg(shot);
  if (sx < 0 || sx > SVG_W || sy < 0 || sy > SVG_H) return null;
  return (
    <circle
      cx={sx}
      cy={sy}
      r={DOT_R}
      fill={shot.made ? DOT_MADE_FILL : DOT_MISS_FILL}
      stroke={DOT_STROKE}
      strokeWidth={1.4}
    />
  );
}

// =============================================================================

function ChartCanvas({ children, ariaLabel }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 524,
        mx: 'auto',
        aspectRatio: `${SVG_W} / ${SVG_H}`,
        borderRadius: 1,
        backgroundImage: `url(${COURT_BG})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
      }}
      role="img"
      aria-label={ariaLabel}
    >
      {children}
    </Box>
  );
}

// =============================================================================

export default function ShotChart({ shots, stats }) {
  const d = useInstatDesign();
  const [filter, setFilter] = useState('all'); // 'all' | '2p' | '3p'

  const filteredShots = useMemo(() => {
    if (filter === '2p') return shots.filter((s) => s.points !== 3);
    if (filter === '3p') return shots.filter((s) => s.points === 3);
    return shots;
  }, [shots, filter]);

  // Classify each shot via Hudl's exact algorithm (court-coord circles/lines,
  // see classifyShot in instatShotZones.js) — no SVG hit-testing required.
  const zoneStats = useMemo(() => {
    const acc = {};
    for (const z of ZONES) acc[z.id] = { made: 0, att: 0 };
    for (const s of filteredShots) {
      const zid = classifyShot(s);
      if (!zid) continue;
      acc[zid].att += 1;
      if (s.made) acc[zid].made += 1;
    }
    return acc;
  }, [filteredShots]);

  const filteredBaseStats = useMemo(() => {
    const total = filteredShots.length;
    const made = filteredShots.filter((s) => s.made).length;
    const twoAtt = filteredShots.filter((s) => s.points !== 3).length;
    const twoMade = filteredShots.filter((s) => s.points !== 3 && s.made).length;
    const threeAtt = filteredShots.filter((s) => s.points === 3).length;
    const threeMade = filteredShots.filter((s) => s.points === 3 && s.made).length;
    return {
      total,
      made,
      fg_pct: total ? Math.round((made / total) * 100) : 0,
      two_att: twoAtt,
      two_made: twoMade,
      two_pct: twoAtt ? Math.round((twoMade / twoAtt) * 100) : 0,
      three_att: threeAtt,
      three_made: threeMade,
      three_pct: threeAtt ? Math.round((threeMade / threeAtt) * 100) : 0,
    };
  }, [filteredShots]);

  if (!shots?.length) {
    return (
      <Card>
        <Typography sx={{ ...d.typo.headline, mb: 1 }}>Shot chart</Typography>
        <Typography sx={{ ...d.typo.caption }}>
          No shots captured for the recent games.
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ ...d.typo.title, color: d.colors.text }}>
            Shot chart
          </Typography>
          <Typography sx={{ ...d.typo.caption }}>
            {filteredShots.length} shots · {stats?.matchCount || '?'} games · 14 zones
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
          <SegmentedControl
            size="sm"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All', count: shots.length },
              { value: '2p',  label: '2PT', count: shots.filter((s) => s.points !== 3).length },
              { value: '3p',  label: '3PT', count: shots.filter((s) => s.points === 3).length },
            ]}
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
          gap: 1.25,
          mb: 2.5,
        }}
      >
        <KpiTile
          label="Field goals"
          value={`${filteredBaseStats.fg_pct}%`}
          suffix={`${filteredBaseStats.made}/${filteredBaseStats.total}`}
          accent
          compact
        />
        <KpiTile
          label="2-point"
          value={`${filteredBaseStats.two_pct}%`}
          suffix={`${filteredBaseStats.two_made}/${filteredBaseStats.two_att}`}
          compact
        />
        <KpiTile
          label="3-point"
          value={`${filteredBaseStats.three_pct}%`}
          suffix={`${filteredBaseStats.three_made}/${filteredBaseStats.three_att}`}
          compact
        />
      </Box>

      {/* Two stacked court views, like Hudl: zones on top, raw shots below. */}
      <Stack spacing={2.5} alignItems="center">
        <Box sx={{ width: '100%', maxWidth: 560 }}>
          <Typography
            sx={{
              ...d.typo.micro,
              textTransform: 'uppercase',
              letterSpacing: 1,
              textAlign: 'center',
              color: d.colors.textSecondary,
              mb: 1,
            }}
          >
            Zones
          </Typography>
          <ChartCanvas ariaLabel="Shot chart by zone">
            <svg
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                overflow: 'visible',
              }}
            >
              {ZONES.map((z) => <ZonePoly key={z.id} zone={z} />)}
              {ZONES.map((z) => (
                <ZoneLabel key={`l-${z.id}`} zone={z} stats={zoneStats[z.id]} />
              ))}
            </svg>
          </ChartCanvas>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 560 }}>
          <Typography
            sx={{
              ...d.typo.micro,
              textTransform: 'uppercase',
              letterSpacing: 1,
              textAlign: 'center',
              color: d.colors.textSecondary,
              mb: 1,
            }}
          >
            Shots on court
          </Typography>
          <ChartCanvas ariaLabel="Individual shots on court">
            <svg
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                overflow: 'visible',
              }}
            >
              {filteredShots.map((s, i) => (
                <ShotDot key={s.id || i} shot={s} />
              ))}
            </svg>
          </ChartCanvas>
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        sx={{ mt: 2, flexWrap: 'wrap', rowGap: 0.75, justifyContent: 'center' }}
      >
        <Pill tone="outline" size="sm">
          {filteredShots.length} shots · {ZONES.length} zones
        </Pill>
        <Pill tone="outline" size="sm">
          ● Made &nbsp;&nbsp; ○ Missed
        </Pill>
      </Stack>
    </Card>
  );
}
